import type { AxiosInstance } from "axios";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { BarList, DualLine, Donut } from "../components/charts.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchHealth, fetchOverview, type AdminHealthBoard, type AdminOverview } from "../lib/admin.js";

/**
 * The staff Overview — the platform at a glance. Every number is drawn from data that
 * already exists (control plane for orgs/stores/plans/MRR, ClickHouse for per-day volume,
 * the health board for the RAG split).
 */
export function OverviewScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [health, setHealth] = useState<AdminHealthBoard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [o, h] = await Promise.all([fetchOverview(client), fetchHealth(client).catch(() => null)]);
      setOverview(o);
      setHealth(h);
      setError(null);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load the overview."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && overview === null) return <p className="text-destructive p-6 text-sm">{error}</p>;
  if (overview === null) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading overview…
      </p>
    );
  }

  const money = (minor: number): string => `${(minor / 100).toLocaleString()} ${overview.currency}`;
  const weekLabel = (iso: string): string => iso.slice(5); // MM-DD
  const dayLabel = (iso: string): string => iso.slice(5);

  return (
    <div className="flex w-full flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Overview</h1>
        <p className="text-muted-foreground text-sm">The whole platform at a glance.</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="MRR" value={money(overview.mrrMinor)} />
        <Kpi label="Organizations" value={overview.orgs.toLocaleString()} />
        <Kpi label="Active stores" value={overview.stores.active.toLocaleString()} />
        <Kpi label="Members" value={overview.members.toLocaleString()} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Store health</CardTitle>
            <CardDescription>{health === null ? "Health board unavailable." : "Live RAG across all stores."}</CardDescription>
          </CardHeader>
          <CardContent>
            {health === null ? (
              <p className="text-muted-foreground text-sm">—</p>
            ) : (
              <Donut
                segments={[
                  { label: "Healthy", value: health.counts.green, className: "stroke-emerald-500" },
                  { label: "Needs attention", value: health.counts.amber, className: "stroke-amber-500" },
                  { label: "Down", value: health.counts.red, className: "stroke-red-500" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Onboarding funnel</CardTitle>
            <CardDescription>Where connected stores are in setup.</CardDescription>
          </CardHeader>
          <CardContent>
            <BarList items={overview.onboarding.map((o) => ({ label: o.state, value: o.count }))} barClassName="bg-blue-500" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Orders &amp; events / day</CardTitle>
            <CardDescription>Platform throughput, last 14 days.</CardDescription>
          </CardHeader>
          <CardContent>
            {overview.ordersByDay.length === 0 && overview.eventsByDay.length === 0 ? (
              <p className="text-muted-foreground text-sm">No volume in the window.</p>
            ) : (
              <DualLine
                a={{ label: `Events (${dayLabel(overview.eventsByDay.at(-1)?.day ?? "")})`, points: overview.eventsByDay.map((d) => d.events), className: "stroke-blue-500" }}
                b={{ label: "Orders", points: overview.ordersByDay.map((d) => d.orders), className: "stroke-emerald-500" }}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">New stores / week</CardTitle>
            <CardDescription>Connected in the last 8 weeks.</CardDescription>
          </CardHeader>
          <CardContent>
            <BarList items={overview.newStoresByWeek.map((w) => ({ label: weekLabel(w.weekStart), value: w.count }))} barClassName="bg-emerald-500" />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Organizations by plan</CardTitle>
            <CardDescription>Live subscriptions per plan.</CardDescription>
          </CardHeader>
          <CardContent>
            {overview.orgsByPlan.length === 0 ? (
              <p className="text-muted-foreground text-sm">No live subscriptions yet.</p>
            ) : (
              <BarList items={overview.orgsByPlan.map((p) => ({ label: p.name, value: p.subscribers }))} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { readonly label: string; readonly value: string }): React.JSX.Element {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}
